import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import CustomerLayout from "../../../layouts/CustomerLayout";
import contactRequests from "../../../api/Customer/ContactRequests";
import {
  ArrowLeft,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Download,
  Users,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MessageSquare,
} from "lucide-react";


const ContactUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);
  const [formData, setFormData] = useState({
    groupName: "",
    selectedFile: null,
  });
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileStats, setFileStats] = useState({
    totalRows: 0,
    estimatedContacts: 0,
    isLargeFile: false,
    processingTime: 0,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [cleanedFile, setCleanedFile] = useState(null);
  const [showCleaningOptions, setShowCleaningOptions] = useState(false);
  const [currentFileName, setCurrentFileName] = useState("");
  const [userChoice, setUserChoice] = useState(null);
  const [showAllFailedRows, setShowAllFailedRows] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);


  const location = useLocation();
  const {locationRoute}=location.state||{};

  // //console.log("Location Route:", location.state?.locationRoute);

  
  const steps = [
    {
      number: 1,
      title: "Upload File",
      description: "Choose your contact file",
    },
    {
      number: 2,
      title: "Validate",
      description: "Process and validate contacts",
    },
    { number: 3, title: "Confirm", description: "Complete the upload" },
  ];

  // File size limits and thresholds
  const FILE_SIZE_LIMITS = {
    MAX_SIZE: 5 * 1024 * 1024,
    LARGE_FILE_THRESHOLD: 2 * 1024 * 1024,
    PREVIEW_ROWS: 10,
    MAX_PREVIEW_ROWS: 1000,
  };

  // Initialize Web Worker for file processing
  const initializeWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    const workerCode = `
      // Import SheetJS in worker
      importScripts('https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js');

      // Normalize phone number - handles scientific notation and various formats
      function normalizePhoneNumber(phone) {
        if (!phone && phone !== 0) return '';
        
        if (typeof phone === 'number') {
          //Handling Big Numbers(safety check)
          if (phone > 1e15) {
            try {
              return BigInt(phone).toString();
            } catch (e) {
              return phone.toFixed(0);
            }
          }
          return phone.toFixed(0);
        }
        const phoneStr = phone.toString().trim();
        if (/^[\\d.]+[eE][+-]?\\d+$/.test(phoneStr)) {
          const numValue = parseFloat(phoneStr);
          if (!isNaN(numValue)) {
            return numValue.toFixed(0);
          }
        }
        return phoneStr;
      }

      // Phone validation in worker
      function validatePhoneNumber(phone) {
        if (!phone && phone !== 0) return { isValid: false, hasSpaces: false, cleanPhone: '' };
        
        // Normalize the phone number first (handles scientific notation)
        const normalizedPhone = normalizePhoneNumber(phone);
        
        if (!normalizedPhone || normalizedPhone.trim() === '') {
          return { isValid: false, hasSpaces: false, cleanPhone: '' };
        }
        
        const phoneStr = normalizedPhone.trim();
        
        // Check if phone number contains spaces
        const hasSpaces = phoneStr.includes(' ');
        
        // Remove all non-digits to get clean phone number
        const cleanPhone = phoneStr.replace(/\\D/g, '');
        
        // If the clean phone number is valid, return success
        if (cleanPhone.length === 12 && cleanPhone.startsWith('233')) {
          return { isValid: true, hasSpaces: hasSpaces, cleanPhone: cleanPhone };
        }
        return { isValid: false, hasSpaces: hasSpaces, cleanPhone: cleanPhone };
      }

      // Check if a row has meaningful content (any non-empty field)
      function hasValidContent(row) {
        if (!row || row.length === 0) return false;
        return row.some(cell => cell && cell.toString().trim() !== '');
      }

      // Check if a row is completely empty
      function isCompletelyEmpty(row) {
        if (!row || row.length === 0) return true;
        return !row.some(cell => cell && cell.toString().trim() !== '');
      }

      // Parses a birthday cell into a strict "YYYY-MM-DD" string.
      // This worker reads cells with raw:true, so a date-formatted Excel cell
      // arrives as a numeric serial value (not a pre-formatted string) - it
      // needs XLSX.SSF to convert that serial number back into a calendar date
      // before the same string validation BulkUploadSection.jsx's worker uses.
      // Blank is always valid (birthday is optional). An unparseable value is
      // NOT treated as a fatal error for the row - the caller decides whether
      // to warn about it, the contact still gets saved without a birthday.
      function parseBirthdayCell(cellValue) {
        if (cellValue === undefined || cellValue === null || cellValue === '') {
          return { formattedBirthday: '', isValid: true };
        }

        let candidateDateString = '';
        if (typeof cellValue === 'number') {
          try {
            candidateDateString = XLSX.SSF.format('yyyy-mm-dd', cellValue);
          } catch (error) {
            return { formattedBirthday: '', isValid: false };
          }
        } else {
          candidateDateString = cellValue.toString().trim();
        }
        if (candidateDateString === '') {
          return { formattedBirthday: '', isValid: true };
        }

        // Normalize common separators ("/" or ".") to "-" before matching.
        const normalizedValue = candidateDateString.replace(/[/.]/g, '-');
        const isoDateMatch = normalizedValue.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
        if (!isoDateMatch) {
          return { formattedBirthday: '', isValid: false };
        }

        const birthdayYear = parseInt(isoDateMatch[1], 10);
        const birthdayMonth = parseInt(isoDateMatch[2], 10);
        const birthdayDay = parseInt(isoDateMatch[3], 10);
        const parsedDate = new Date(birthdayYear, birthdayMonth - 1, birthdayDay);
        const isRealCalendarDate =
          parsedDate.getFullYear() === birthdayYear &&
          parsedDate.getMonth() === birthdayMonth - 1 &&
          parsedDate.getDate() === birthdayDay;
        const isInReasonableRange = birthdayYear >= 1900 && parsedDate <= new Date();

        if (!isRealCalendarDate || !isInReasonableRange) {
          return { formattedBirthday: '', isValid: false };
        }

        return { formattedBirthday: normalizedValue, isValid: true };
      }

      // Process Excel file using SheetJS
      function processExcelFile(file) {
        const reader = new FileReader();
        reader.onload = function(e) {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '',
              raw: true  // Use raw: true to get actual numeric values, then we'll normalize them
            });
            processRowData(jsonData, file.name);
          } catch (error) {
            self.postMessage({
              type: 'error',
              error: 'Failed to parse Excel file: ' + error.message
            });
          }
        };
        reader.readAsArrayBuffer(file);
      }

      // Process row data with optimized logic to prevent duplication
      function processRowData(data, fileName) {
        let validContacts = [];
        let failedRows = [];
        let birthdayWarnings = [];
        let totalRows = data.length;
        let processedRows = 0;
        let emptyRowsCleaned = 0;

        // Use Set to track processed phone numbers and prevent duplicates
        const processedPhones = new Set();

        data.forEach((row, index) => {
          processedRows++;

          // Skip header row
          if (index === 0) return;

          // Skip completely empty rows and count them
          if (isCompletelyEmpty(row)) {
            emptyRowsCleaned++;
            return;
          }

          // If row has content, process it
          if (hasValidContent(row)) {
            // Normalize phone number to handle scientific notation and numeric types
            const rawPhone = row[0];
            const phoneNumber = normalizePhoneNumber(rawPhone);
            const firstName = row[1] ? row[1].toString().trim() : '';
            const lastName = row[2] ? row[2].toString().trim() : '';
            const email = row[3] ? row[3].toString().trim() : '';
            const birthdayResult = parseBirthdayCell(row[4]);
            if (!birthdayResult.isValid) {
              birthdayWarnings.push({ rowNumber: index + 1, phoneNumber: phoneNumber, rawBirthdayValue: row[4] });
            }

            // Check if phone number is missing but other data exists
            if (phoneNumber === '' && (firstName !== '' || lastName !== '' || email !== '' || birthdayResult.formattedBirthday !== '')) {
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber || '[Empty]',
                reason: 'Missing phone number but has other contact data',
                hasOtherData: true,
                firstName: firstName,
                lastName: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday
              });
              return;
            }
            
            // Validate phone number
            const validation = validatePhoneNumber(phoneNumber);
            
            // Check if the cleaned phone number is valid (12 digits starting with 233)
            const isCleanPhoneValid = validation.cleanPhone.length === 12 && validation.cleanPhone.startsWith('233');
            
            if (isCleanPhoneValid && !validation.hasSpaces) {
              // Only consider valid if no spaces and clean phone is valid
              // Check for duplicate phone numbers
              if (processedPhones.has(validation.cleanPhone)) {
                failedRows.push({
                  rowNumber: index + 1,
                  phoneNumber: phoneNumber,
                  reason: 'Duplicate phone number'
                });
                return;
              }
              
              // Add to processed phones set
              processedPhones.add(validation.cleanPhone);
              
              // Add valid contact
              validContacts.push({
                phone_number: validation.cleanPhone,
                first_name: firstName,
                last_name: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday,
                hadSpaces: false
              });
            } else if (isCleanPhoneValid && validation.hasSpaces) {
              // Phone number would be valid if spaces were removed - add to failed rows
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber,
                reason: 'Phone number contains spaces - can be cleaned',
                hasSpaces: true,
                cleanPhone: validation.cleanPhone, // Store the cleaned version for later use
                firstName: firstName,
                lastName: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday
              });
            } else {
              let reason;
              if (phoneNumber === '') {
                reason = 'Empty phone number';
              } else if (validation.cleanPhone.length !== 12) {
                reason = \`Invalid phone number (\${validation.cleanPhone.length} digits, need 12)\`;
              } else if (!validation.cleanPhone.startsWith('233')) {
                reason = 'Phone number must start with 233';
              } else {
                reason = 'Invalid phone number format';
              }
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber,
                reason: reason,
                hasSpaces: validation.hasSpaces
              });
            }
          }
          
          // Report progress every 1000 rows or at the end
          if (processedRows % 1000 === 0 || processedRows === totalRows) {
            const progress = (processedRows / totalRows) * 100;
            self.postMessage({
              type: 'progress',
              progress: progress,
              processed: processedRows
            });
          }
        });

        self.postMessage({
          type: 'validationComplete',
          data: {
            validContacts,
            failedRows,
            birthdayWarnings,
            totalProcessed: totalRows,
            successCount: validContacts.length,
            failedCount: failedRows.length,
            emptyRowsCleaned: emptyRowsCleaned
          }
        });
      }

      function isExcelFile(fileName) {
        return /\\.(xlsx|xls)$/i.test(fileName);
      }

      self.onmessage = function(e) {
        const { file, action } = e.data;
        
        if (action === 'validateContacts') {
          if (isExcelFile(file.name)) {
            processExcelFile(file);
          } else {
            self.postMessage({
              type: 'error',
              error: 'Unsupported file format. Please use Excel files (.xlsx or .xls)'
            });
          }
        } else if (action === 'analyzeFile') {
          if (isExcelFile(file.name)) {
            const reader = new FileReader();
            reader.onload = function(e) {
              try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
                const totalRows = range.e.r + 1;
                
                self.postMessage({
                  type: 'analysisComplete',
                  data: {
                    totalRows: totalRows,
                    estimatedContacts: Math.max(0, totalRows - 1),
                    isLargeFile: file.size > 2 * 1024 * 1024
                  }
                });
              } catch (error) {
                self.postMessage({
                  type: 'error',
                  error: 'Failed to analyze Excel file: ' + error.message
                });
              }
            };
            reader.readAsArrayBuffer(file);
          } else {
            self.postMessage({
              type: 'error',
              error: 'Unsupported file format. Please use Excel files (.xlsx or .xls)'
            });
          }
        }
      };
    `;

    const blob = new Blob([workerCode], { type: "application/javascript" });
    workerRef.current = new Worker(URL.createObjectURL(blob));

    workerRef.current.onmessage = (e) => {
      const { type, data, error, progress } = e.data;

      if (type === "validationComplete") {
        setValidationResults(data);
        setFileStats((prev) => ({
          ...prev,
          totalRows: data.totalProcessed,
          estimatedContacts: data.successCount,
          processingTime: Date.now() - processingStartTime,
        }));

        // Check if any failed rows have spaces that can be cleaned
        const failedRowsWithSpaces = data.failedRows.filter(
          (row) => row.hasSpaces && row.cleanPhone
        ).length;
        const totalSpacesIssues = failedRowsWithSpaces;

        if (
          data.successCount === 0 &&
          data.failedRows.filter((row) => row.hasSpaces && row.cleanPhone)
            .length === 0
        ) {
          setValidationErrors([
            "No valid phone numbers found in the file.",
            "Phone numbers must be 12 digits and start with 233.",
            "Please check your file format and try again.",
            "Kindly download the template for the correct format.",
          ]);
          setShowCleaningOptions(false);
          setUserChoice(null);
          toast.error("No valid phone numbers found in the file.");
          // //console.log("Validation Error: No valid phone numbers found.");
          
        } else if (
          data.failedCount > 0 ||
          totalSpacesIssues > 0 ||
          data.failedRows.some((row) => row.hasSpaces)
        ) {
          const originalFileName =
            formData.selectedFile?.name || currentFileName || "contacts.xlsx";

          // Create enhanced valid contacts by including phone numbers with spaces that can be cleaned
          const enhancedValidContacts = [...data.validContacts];

          // Add failed rows with spaces that can be cleaned
          data.failedRows.forEach((failedRow) => {
            if (failedRow.hasSpaces && failedRow.cleanPhone) {
              enhancedValidContacts.push({
                phone_number: failedRow.cleanPhone,
                first_name: failedRow.firstName || "",
                last_name: failedRow.lastName || "",
                email: failedRow.email || "",
                birthday: failedRow.birthday || "",
                hadSpaces: true,
              });
            }
          });

          const cleaned = createCleanedFile(
            enhancedValidContacts,
            originalFileName
          );
          setCleanedFile(cleaned);
          setShowCleaningOptions(true);
          setUserChoice(null);

          const emptyRowsMessage =
            data.emptyRowsCleaned > 0
              ? ` (${data.emptyRowsCleaned} empty rows automatically cleaned)`
              : "";

          let spacesMessage = "";
          if (failedRowsWithSpaces > 0) {
            spacesMessage += ` ${failedRowsWithSpaces} phone number${
              failedRowsWithSpaces > 1 ? "s" : ""
            } contain${
              failedRowsWithSpaces > 1 ? "" : "s"
            } spaces and can be cleaned`;
          }

          setValidationErrors([
            `Found ${data.successCount} valid contacts and ${data.failedCount} invalid entries${emptyRowsMessage}.${spacesMessage}`,
            "Please choose how to proceed below.",
          ]);
        } else {
          if (data.emptyRowsCleaned > 0) {
            const originalFileName =
              formData.selectedFile?.name || currentFileName || "contacts.xlsx";
            const fullyCleanedFile = createFullyCleanedFile(
              data.validContacts,
              originalFileName
            );
            setFormData((prev) => ({
              ...prev,
              selectedFile: fullyCleanedFile,
            }));
            setValidationErrors([
              `${data.emptyRowsCleaned} empty rows were automatically cleaned from your file.`,
              "A cleaned version of your file will be uploaded (no empty rows).",
            ]);
          } else {
            setValidationErrors([]);
          }
          setShowCleaningOptions(false);
          setUserChoice("cleaned");
        }

        // Non-blocking heads-up about birthday cells that couldn't be parsed
        // (the affected contacts are still saved, just without a birthday).
        if (data.birthdayWarnings && data.birthdayWarnings.length > 0) {
          setValidationErrors((previousErrors) => [
            ...previousErrors,
            `${data.birthdayWarnings.length} row(s) had a birthday we couldn't understand and might be saved without one (expected format: YYYY-MM-DD).`,
          ]);
        }

        setShowResults(true);
        setIsProcessing(false);
        setProcessingProgress(100);
      } else if (type === "analysisComplete") {
        setFileStats({
          totalRows: data.totalRows,
          estimatedContacts: data.estimatedContacts,
          isLargeFile: data.isLargeFile,
          processingTime: 0,
        });
      } else if (type === "progress") {
        setProcessingProgress(progress);
      } else if (type === "error") {
        setValidationErrors([error]);
        setErrors((prev) => ({ ...prev, file: error }));
        setIsProcessing(false);
      }
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let processingStartTime = 0;

  const PerformanceIndicator = ({ fileSize, isLargeFile }) => {
    const getPerformanceLevel = () => {
      if (fileSize < 1024 * 1024) return { level: "excellent", color: "red" };
      if (fileSize < 2 * 1024 * 1024) return { level: "good", color: "blue" };
      if (fileSize < 5 * 1024 * 1024)
        return { level: "moderate", color: "yellow" };
      return { level: "heavy", color: "red" };
    };

    const performance = getPerformanceLevel();

    return (
      <div className="flex gap-2 items-center text-xs sm:text-sm">
        <div
          className={`w-2 h-2 rounded-full ${
            performance.color === "red"
              ? "bg-red-500"
              : performance.color === "blue"
              ? "bg-blue-500"
              : performance.color === "yellow"
              ? "bg-yellow-500"
              : "bg-red-500"
          }`}
        />
        <span className="text-gray-600">
          Performance: {performance.level}
          {isLargeFile && " (background processing)"}
        </span>
      </div>
    );
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle dropped files
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    // Reset previous state
    setErrors({});
    setValidationResults(null);
    setShowResults(false);
    setValidationErrors([]);
    setCleanedFile(null);
    setShowCleaningOptions(false);
    setUserChoice(null);
    setShowAllFailedRows(false);
    setOriginalFile(null);
    setFileStats({
      totalRows: 0,
      estimatedContacts: 0,
      isLargeFile: false,
      processingTime: 0,
    });

    // Validate file
    if (!file) return;

    // Enhanced file type validation
    const validTypes = [
      "application/vnd.ms-excel", // Excel .xls
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Excel .xlsx
    ];

    const validExtensions = /\.(xls|xlsx)$/i;
    const fileExtension = file.name.toLowerCase();

    // Check if it's an image file
    if (
      file.type.startsWith("image/") ||
      /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileExtension)
    ) {
      setErrors({
        file: "Image files are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Check if it's a Word document
    if (file.type.includes("word") || /\.(doc|docx)$/i.test(fileExtension)) {
      setErrors({
        file: "Word documents are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Check if it's a PDF
    if (file.type === "application/pdf" || fileExtension.endsWith(".pdf")) {
      setErrors({
        file: "PDF files are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Check if it's a text/CSV file
    if (
      file.type === "text/csv" ||
      file.type === "text/plain" ||
      fileExtension.endsWith(".csv") ||
      fileExtension.endsWith(".txt")
    ) {
      setErrors({
        file: "CSV and text files are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Check if it's a PowerPoint file
    if (
      file.type.includes("powerpoint") ||
      /\.(ppt|pptx)$/i.test(fileExtension)
    ) {
      setErrors({
        file: "PowerPoint files are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Check if it's a ZIP or compressed file
    if (
      file.type.includes("zip") ||
      file.type.includes("compressed") ||
      /\.(zip|rar|7z|tar|gz)$/i.test(fileExtension)
    ) {
      setErrors({
        file: "Compressed files are not supported. Please upload an Excel file (.xlsx or .xls)",
      });
      return;
    }

    // Final check for Excel files
    if (!validTypes.includes(file.type) && !validExtensions.test(file.name)) {
      setErrors({
        file: "Please select a valid Excel file (.xlsx or .xls). Other file types are not supported.",
      });
      return;
    }

    // File size validation
    if (file.size > FILE_SIZE_LIMITS.MAX_SIZE) {
      setErrors({
        file: `File size must be less than ${Math.round(
          FILE_SIZE_LIMITS.MAX_SIZE / (1024 * 1024)
        )}MB. Your file is ${formatFileSize(file.size)}.`,
      });
      return;
    }

    // Set file and start processing
    setFormData((prev) => ({ ...prev, selectedFile: file }));
    setOriginalFile(file); // Store original file for potential restoration
    setCurrentFileName(file.name);
    setIsProcessing(true);
    setProcessingProgress(0);
    processingStartTime = Date.now();

    // Initialize worker and process file
    initializeWorker();

    // Start file analysis first
    setTimeout(() => {
      if (workerRef.current) {
        workerRef.current.postMessage({
          file,
          action: "analyzeFile",
        });
      }
    }, 100);

    // Start validation after analysis
    setTimeout(() => {
      if (workerRef.current) {
        workerRef.current.postMessage({
          file,
          action: "validateContacts",
        });
      }
    }, 500);
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.groupName.trim()) {
      newErrors.groupName = "Group name is required";
    }

    if (!formData.selectedFile) {
      newErrors.file = "Please select a file to upload";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      //Todo => Send Excel file to the API
      const response = await contactRequests.uploadContactFile(
        formData.selectedFile,
        formData.groupName
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

     if (
  response.success &&
  (locationRoute?.includes("/home") || locationRoute?.includes("/quick-sms")||locationRoute?.includes("/contact-group")||locationRoute?.includes("/contacts/upload")||locationRoute?.includes("/contacts"))
) {
  toast.success("File uploaded successfully! Processing contacts...");

  setTimeout(() => {
    navigate("/contacts");
  }, 2000);
}

      else if (locationRoute?.includes("/bulk-sms")) {
         toast.success("File uploaded successfully! Processing contacts...");
      navigate("/bulk-sms");
      }
      //commit
      else {
        toast.error(response.error?.message || "Upload failed");
      }
    } catch (error) {
      toast.error("Error uploading contacts");
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  //Todo => Handle template download
  const handleDownloadTemplate = async () => {
    const link = document.createElement("a");
    link.href = "/docs/contact_template_format.xlsx";
    link.download = "contact_template_format.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  // Create cleaned Excel file with only valid contacts (no empty rows, no invalid data)
  const createCleanedFile = (validContacts, originalFileName) => {
    // Create header row
    const headers = ["Phone Number", "First Name", "Last Name", "Email", "Birthday"];

    // Create data rows from valid contacts - ensure phone numbers are strings
    // Phone numbers are already normalized (no scientific notation) from validation
    const dataRows = validContacts.map((contact) => [
      String(contact.phone_number || ""), // Explicitly convert to string
      contact.first_name || "",
      contact.last_name || "",
      contact.email || "",
      contact.birthday || "",
    ]);

    // Combine headers and data
    const worksheetData = [headers, ...dataRows];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Optimize worksheet by removing empty cells and setting column widths
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
        
        // Format phone number column (column A, row > 0) as text to prevent scientific notation
        if (C === 0 && R > 0 && ws[cell_address]) {
          // Set cell format to text (@) to prevent Excel from converting to scientific notation
          ws[cell_address].z = "@"; // @ is Excel's text format code
          // Ensure the value is stored as a string
          if (typeof ws[cell_address].v === "number") {
            ws[cell_address].v = ws[cell_address].v.toString();
            ws[cell_address].t = "s"; // Set type to string
          } else if (typeof ws[cell_address].v === "string") {
            ws[cell_address].t = "s"; // Set type to string
          }
        }

        // Format birthday column (column E, row > 0) as text too, so re-opening
        // or re-uploading this file doesn't let Excel reinterpret "YYYY-MM-DD"
        // as a date serial number.
        if (C === 4 && R > 0 && ws[cell_address]) {
          ws[cell_address].z = "@";
          ws[cell_address].t = "s";
        }

        if (!ws[cell_address] || ws[cell_address].v === "") {
          delete ws[cell_address];
        }
      }
    }

    // Set column widths for better file optimization
    ws["!cols"] = [
      { width: 15 }, // Phone Number
      { width: 15 }, // First Name
      { width: 15 }, // Last Name
      { width: 25 }, // Email
      { width: 15 }, // Birthday
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Cleaned Contacts");

    // Generate file buffer with optimized options
    // Note: We keep cellStyles enabled to preserve text formatting for phone numbers
    const fileBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      compression: true, // Enable compression
      bookSST: false, // Disable shared strings for smaller files
      cellStyles: true, // Enable cell styles to preserve text format for phone numbers
    });

    // Create blob and file
    const blob = new Blob([fileBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const cleanedFileName = originalFileName.replace(
      /\.(xlsx|xls)$/i,
      "_cleaned.xlsx"
    );

    return new File([blob], cleanedFileName, { type: blob.type });
  };

  // Create cleaned file with all valid data (including empty row removal)
  const createFullyCleanedFile = (validContacts, originalFileName) => {
    // This creates a file with ONLY valid contacts and NO empty rows
    return createCleanedFile(validContacts, originalFileName);
  };

  // Download cleaned file
  const downloadCleanedFile = () => {
    if (!cleanedFile) return;

    const url = URL.createObjectURL(cleanedFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = cleanedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Proceed with cleaned file
  const proceedWithCleanedFile = () => {
    if (!cleanedFile) return;

    // Monitor file size changes to prevent ballooning
    const originalSize = originalFile ? originalFile.size : 0;
    const cleanedSize = cleanedFile.size;
    const sizeRatio = cleanedSize / originalSize;

    // //console.log("File size monitoring:", {
    //   originalFile: originalFile?.name,
    //   originalSize: formatFileSize(originalSize),
    //   cleanedFile: cleanedFile.name,
    //   cleanedSize: formatFileSize(cleanedSize),
    //   sizeRatio: sizeRatio.toFixed(2),
    //   sizeChange:
    //     sizeRatio > 1.5 ? "WARNING: File size increased significantly" : "OK",
    // });

    // Warn if file size increased significantly (more than 50%)
    if (sizeRatio > 1.5) {
      console.warn("File size ballooning detected!", {
        originalSize: formatFileSize(originalSize),
        cleanedSize: formatFileSize(cleanedSize),
        increase: `${((sizeRatio - 1) * 100).toFixed(1)}%`,
      });

      // Show warning to user
      toast.warning(
        `File size increased from ${formatFileSize(
          originalSize
        )} to ${formatFileSize(
          cleanedSize
        )}. This may affect upload performance.`
      );
    }

    setFormData((prev) => ({ ...prev, selectedFile: cleanedFile }));
    setShowCleaningOptions(false);
    setValidationErrors([]);
    setErrors((prev) => ({ ...prev, file: null }));
    setUserChoice("cleaned");

    // Calculate total contacts after cleaning (including spaces-cleaned contacts)
    const spacesCleanedCount =
      validationResults?.failedRows?.filter(
        (row) => row.hasSpaces && row.cleanPhone
      ).length || 0;
    const totalContactsAfterCleaning =
      validationResults.successCount + spacesCleanedCount;

    // Show success message
    toast.success(
      `File cleaned! ${totalContactsAfterCleaning} valid contacts ready for upload (${spacesCleanedCount} had spaces removed).`
    );
  };

  // Proceed with original file
  const proceedWithOriginalFile = () => {
    // Check if there are any phone numbers with spaces that can be cleaned
    const failedRowsWithSpaces =
      validationResults?.failedRows?.filter(
        (row) => row.hasSpaces && row.cleanPhone
      ).length || 0;
    const totalSpacesIssues = failedRowsWithSpaces;

    if (totalSpacesIssues > 0) {
      // Prevent proceeding with original file when spaces are detected
      toast.error(
        `Cannot proceed with original file. ${totalSpacesIssues} phone number${
          totalSpacesIssues > 1 ? "s" : ""
        } contain${
          totalSpacesIssues > 1 ? "" : "s"
        } spaces. Please use the cleaned data instead.`
      );
      return;
    }

    // Restore the original file (with invalid data and empty rows)
    if (originalFile) {
      setFormData((prev) => ({ ...prev, selectedFile: originalFile }));
      // //console.log("Restored original file:", originalFile.name);
    }

    setShowCleaningOptions(false);
    setValidationErrors([]);
    setErrors((prev) => ({ ...prev, file: null }));
    setUserChoice("original");

    // Show info message
    toast.info(
      `Proceeding with original file (includes invalid data and empty rows). Backend will handle validation.`
    );
  };

  return (
    <CustomerLayout>
      <div className="py-4 pt-16 min-h-screen bg-gray-50 sm:py-6 sm:pt-20">
        <div className="px-3 mx-auto max-w-8xl sm:px-4 md:px-6 lg:px-8">
          {/* Header */}
          <div className="p-4 mb-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6 sm:mb-6">
            <div className="flex gap-3 items-center mb-4 sm:gap-4 sm:mb-6">
              <button
                onClick={() => navigate("/contacts")}
                className="flex-shrink-0 p-2 text-gray-600 rounded-lg transition-colors duration-200 hover:text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate sm:text-2xl lg:text-3xl">
                  Upload Contacts
                </h1>
                <p className="mt-1 text-sm text-gray-600 sm:text-base line-clamp-2">
                  Add contacts to your account by uploading a XLSX or XLS file
                </p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-6 sm:mb-8">
              {/* Mobile Progress Steps - Vertical */}
              <div className="flex flex-col space-y-4 sm:hidden">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex relative items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full border-2 flex-shrink-0 ${
                        (showResults && step.number <= 2) ||
                        (uploading && step.number <= 3)
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-300 text-gray-400"
                      }`}
                    >
                      {(showResults && step.number <= 2) ||
                      (uploading && step.number <= 3) ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-sm font-semibold">
                          {step.number}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 ml-3 min-w-0">
                      <p
                        className={`font-medium text-sm ${
                          (showResults && step.number <= 2) ||
                          (uploading && step.number <= 3)
                            ? "text-gray-900"
                            : "text-gray-400"
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {step.description}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`absolute left-4 top-8 w-0.5 h-4 ${
                          (showResults && step.number < 2) ||
                          (uploading && step.number < 3)
                            ? "bg-blue-600"
                            : "bg-gray-200"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Progress Steps - Horizontal */}
              <div className="hidden justify-between items-center sm:flex">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                        (showResults && step.number <= 2) ||
                        (uploading && step.number <= 3)
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-300 text-gray-400"
                      }`}
                    >
                      {(showResults && step.number <= 2) ||
                      (uploading && step.number <= 3) ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <span className="font-semibold">{step.number}</span>
                      )}
                    </div>
                    <div className="ml-3">
                      <p
                        className={`font-medium ${
                          (showResults && step.number <= 2) ||
                          (uploading && step.number <= 3)
                            ? "text-gray-900"
                            : "text-gray-400"
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="hidden text-sm text-gray-500 md:block">
                        {step.description}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 lg:mx-8 ${
                          (showResults && step.number < 2) ||
                          (uploading && step.number < 3)
                            ? "bg-blue-600"
                            : "bg-gray-200"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && uploadProgress > 0 && (
            <div className="p-4 mb-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6 sm:mb-6">
              <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="flex gap-3 items-center">
                  <div className="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin sm:h-6 sm:w-6"></div>
                  <span className="text-sm font-medium text-gray-900 sm:text-base">
                    <span className="hidden sm:inline">
                      Uploading {formatNumber(fileStats.estimatedContacts)}{" "}
                      contacts...
                    </span>
                    <span className="sm:hidden">Uploading contacts...</span>
                  </span>
                </div>
                <span className="self-start text-sm text-gray-500 sm:self-auto">
                  {uploadProgress}%
                </span>
              </div>

              <div className="w-full h-3 bg-gray-200 rounded-full">
                <div
                  className="h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 sm:gap-6">
            {/* Main Upload Form */}
            <div className="space-y-4 lg:col-span-2 sm:space-y-6">
              {/* Group Information */}
              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 sm:text-xl">
                 Contact Group Information
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Name of Group *
                    </label>
                    <input
                      type="text"
                      value={formData.groupName}
                      onChange={(e) =>
                        handleInputChange("groupName", e.target.value)
                      }
                      placeholder="Enter a descriptive name for this contact group"
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base ${
                        errors.groupName
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                      disabled={uploading || isProcessing}
                    />
                    {errors.groupName && (
                      <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errors.groupName}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
                <h2 className="mb-4 text-lg font-bold text-gray-900 sm:text-xl">
                  Upload File
                </h2>

                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 transition-all duration-200 ${
                    dragActive
                      ? "border-blue-400 bg-blue-50"
                      : errors.file
                      ? "border-amber-300 bg-amber-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    accept=".xls,.xlsx"
                    className="hidden"
                    disabled={uploading || isProcessing}
                  />

                  <div className="text-center">
                    {formData.selectedFile ? (
                      <div className="flex flex-col gap-3 justify-center items-center sm:flex-row sm:gap-4">
                        <div
                          className={`p-3 sm:p-4 rounded-lg sm:rounded-xl flex-shrink-0 ${
                            fileStats.isLargeFile
                              ? "bg-yellow-100"
                              : "bg-red-100"
                          }`}
                        >
                          <FileText
                            className={`w-6 h-6 sm:w-8 sm:h-8 ${
                              fileStats.isLargeFile
                                ? "text-yellow-600"
                                : "text-red-600"
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0 text-center sm:text-left">
                          <div className="flex flex-wrap gap-2 items-center">
                            <p
                              className="text-base font-medium text-gray-900 truncate sm:text-lg"
                              title={formData.selectedFile.name}
                            >
                              {formData.selectedFile.name}
                            </p>
                            {(formData.selectedFile.name.includes("_cleaned") ||
                              userChoice === "cleaned") && (
                              <span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                                ✓ Cleaned
                              </span>
                            )}
                            {validationResults &&
                              validationResults.emptyRowsCleaned > 0 && (
                                <span className="px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
                                  {validationResults.emptyRowsCleaned} empty
                                  rows removed
                                </span>
                              )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(formData.selectedFile.size)}
                          </p>
                          {formData.selectedFile && (
                            <PerformanceIndicator
                              fileSize={formData.selectedFile.size}
                              isLargeFile={fileStats.isLargeFile}
                            />
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              selectedFile: null,
                            }));
                            setShowResults(false);
                            setValidationResults(null);
                            setValidationErrors([]);
                            setCleanedFile(null);
                            setShowCleaningOptions(false);
                            setCurrentFileName("");
                            setUserChoice(null);
                            setShowAllFailedRows(false);
                            setOriginalFile(null);
                            setErrors((prev) => ({ ...prev, file: null }));
                            setFileStats({
                              totalRows: 0,
                              estimatedContacts: 0,
                              isLargeFile: false,
                              processingTime: 0,
                            });
                          }}
                          className="flex-shrink-0 p-2 text-red-600 rounded-lg transition-colors duration-200 hover:bg-red-100"
                          disabled={isProcessing}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="inline-block p-4 mb-3 bg-gray-100 rounded-lg sm:p-6 sm:rounded-xl sm:mb-4">
                          <Upload className="w-8 h-8 text-gray-400 sm:w-10 sm:h-10 lg:w-12 lg:h-12" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 sm:text-xl">
                          <span className="hidden sm:inline">
                            Drop your file here, or{" "}
                          </span>
                          <button className="text-grey-900">
                            <span className="sm:hidden"
                            onClick={() => fileInputRef.current?.click()}
                            >Tap to </span>browse
                          </button>
                        </h3>
                        <p className="mb-4 text-[0.75rem] text-gray-400 sm:text-base">
                          Upload an Excel file (.xlsx) with your contacts. The
                          file should have columns for:
                          <strong>
                            {" "}
                            Phone Number, First Name, Last Name, Email, Birthday
                          </strong>{" "}
                          (in that order).
                          <br />
                          <span className="text-gray-400">
                            Birthday is optional — format as YYYY-MM-DD.
                          </span>
                          {/* <span className="font-medium text-orange-600">
                            Phone numbers must be 13 digits starting with 233.
                            Spaces will be automatically removed.
                          </span> */}
                        </p>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
                          disabled={uploading || isProcessing}
                        >
                          Choose File
                        </button>
                      </>
                    )}
                  </div>

                  {/* Processing Progress */}
                  {isProcessing && (
                    <div className="p-4 mt-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center">
                        <div className="flex gap-3 items-center">
                          <div className="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin sm:h-6 sm:w-6"></div>
                          <span className="text-sm font-medium text-gray-900 sm:text-base">
                            Processing file...
                          </span>
                        </div>
                        {fileStats.isLargeFile && (
                          <div className="flex gap-2 items-center px-3 py-1 text-xs text-yellow-800 bg-yellow-100 rounded-full sm:text-sm">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">
                              Large file detected
                            </span>
                            <span className="sm:hidden">Large file</span>
                          </div>
                        )}
                      </div>

                      {formData.selectedFile && (
                        <PerformanceIndicator
                          fileSize={formData.selectedFile.size}
                          isLargeFile={fileStats.isLargeFile}
                        />
                      )}

                      <div className="mt-4 w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full transition-all duration-300"
                          style={{ width: `${processingProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {(errors.file || validationErrors.length > 0) && (
                    <div className="p-3 mt-4 bg-amber-50 rounded-lg border border-amber-200">
                      {errors.file && (
                        <p className="flex gap-2 items-center mb-2 text-sm text-amber-700">
                          <AlertCircle className="flex-shrink-0 w-4 h-4" />
                          {errors.file}
                        </p>
                      )}
                      {validationErrors.length > 0 && (
                        <div className="space-y-1">
                          {validationErrors.map((error, index) => (
                            <p
                              key={index}
                              className="flex gap-2 items-center text-sm text-amber-700"
                            >
                              <AlertTriangle className="flex-shrink-0 w-4 h-4" />
                              {error}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Results */}
              {showResults && validationResults && (
                <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
                  <div className="flex flex-col gap-2 mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                      Validation Results
                    </h2>
                    <div className="flex flex-col gap-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:gap-4 sm:text-sm">
                      <span>
                        {formatNumber(validationResults.totalProcessed)} rows
                        processed
                      </span>
                      {validationResults.emptyRowsCleaned > 0 && (
                        <span className="text-blue-600">
                          • {formatNumber(validationResults.emptyRowsCleaned)}{" "}
                          empty rows cleaned
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Empty Rows Cleaned Notice */}
                  {validationResults.emptyRowsCleaned > 0 && (
                    <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex gap-3 items-center">
                        <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            File Cleaned Successfully
                          </p>
                          <p className="text-xs text-blue-600">
                            {formatNumber(validationResults.emptyRowsCleaned)}{" "}
                            empty rows were automatically removed from your file
                            to prevent database bloat
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Success/Failure Summary */}
                  <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex gap-3 items-center">
                        <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            Total Contacts
                          </p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatNumber(validationResults.totalProcessed - 1)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex gap-3 items-center">
                        <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                          <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            Valid Contacts
                          </p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatNumber(validationResults.successCount)}
                          </p>
                          {validationResults.failedRows?.filter(
                            (row) => row.hasSpaces && row.cleanPhone
                          ).length > 0 && (
                            <p className="mt-1 text-xs text-blue-600">
                              {
                                validationResults.failedRows.filter(
                                  (row) => row.hasSpaces && row.cleanPhone
                                ).length
                              }{" "}
                              can be cleaned
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex gap-3 items-center">
                        <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
                          <XCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-red-800">
                            Failed Contacts
                          </p>
                          <p className="text-2xl font-bold text-red-900">
                            {formatNumber(validationResults.failedCount)}
                          </p>
                          {validationResults.failedRows?.filter(
                            (row) => row.hasSpaces
                          ).length > 0 && (
                            <p className="mt-1 text-xs text-red-600">
                              {
                                validationResults.failedRows.filter(
                                  (row) => row.hasSpaces
                                ).length
                              }{" "}
                              contain spaces
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Failed Rows Details */}
                  {validationResults.failedCount > 0 && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-sm font-medium text-red-800">
                          Failed Rows Details
                        </h3>
                        {/* Removed as per edit hint */}
                      </div>
                      <div className="overflow-y-auto max-h-40">
                        <div className="space-y-2">
                          {validationResults.failedRows
                            .slice(
                              0,
                              showAllFailedRows
                                ? validationResults.failedRows.length
                                : 5
                            )
                            .map((failedRow, index) => (
                              <div key={index} className="space-y-1">
                                <div className="flex justify-between items-center text-xs">
                                  <div className="flex gap-2 items-center">
                                    <span className="text-red-700">
                                      Row {failedRow.rowNumber}:{" "}
                                      {failedRow.phoneNumber}
                                    </span>
                                    {/* Removed as per edit hint */}
                                  </div>
                                  <span className="text-red-600">
                                    {failedRow.reason}
                                  </span>
                                </div>
                                {failedRow.hasOtherData && (
                                  <div className="ml-4 text-xs italic text-red-500">
                                    Data:{" "}
                                    {[
                                      failedRow.firstName,
                                      failedRow.lastName,
                                      failedRow.email,
                                    ]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                      {validationResults.failedRows.length > 5 && (
                        <div className="pt-3 mt-3 border-t border-red-200">
                          <button
                            onClick={() =>
                              setShowAllFailedRows(!showAllFailedRows)
                            }
                            className="flex gap-1 items-center text-xs font-medium text-red-600 transition-colors duration-200 hover:text-red-800"
                          >
                            {showAllFailedRows ? (
                              <>
                                Show Less
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 15l7-7 7 7"
                                  />
                                </svg>
                              </>
                            ) : (
                              <>
                                Show {validationResults.failedRows.length - 5}{" "}
                                More Failed Rows
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Processing Performance */}
                  <div className="p-3 mt-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <div className="flex gap-2 items-center">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800">
                          Processed{" "}
                          {formatNumber(validationResults.totalProcessed)} rows
                          in {(fileStats.processingTime / 1000).toFixed(1)}s
                        </span>
                      </div>
                      {validationResults.emptyRowsCleaned > 0 && (
                        <div className="flex gap-2 items-center">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-blue-800">
                            Cleaned{" "}
                            {formatNumber(validationResults.emptyRowsCleaned)}{" "}
                            empty rows
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* File Cleaning Options */}
              {showCleaningOptions && (
                <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
                  <div className="flex gap-3 items-center mb-4">
                    <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        File Cleaning Options
                      </h3>
                      <p className="text-sm text-gray-600">
                        Choose how to handle invalid entries
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Download Cleaned File Option */}
                    <div className="p-4 rounded-lg border border-gray-200 transition-colors hover:border-blue-300">
                      <div className="flex gap-3 items-center mb-3">
                        <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-lg">
                          <Download className="w-4 h-4 text-red-600" />
                        </div>
                        <h4 className="font-medium text-gray-900">
                          Get Cleaned File
                        </h4>
                      </div>
                      <p className="mb-4 text-sm text-gray-600">
                        Download a new Excel file with valid contacts. You can
                        review and fix any issues manually.
                      </p>
                      <button
                        onClick={downloadCleanedFile}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Get Cleaned File
                      </button>
                    </div>

                    {/* Proceed with Cleaned Data Option */}
                    <div className="p-4 rounded-lg border border-gray-200 transition-colors hover:border-blue-300">
                      <div className="flex gap-3 items-center mb-3">
                        <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                        </div>
                        <h4 className="font-medium text-gray-900">
                          Use Cleaned Data
                        </h4>
                      </div>
                      <p className="mb-4 text-sm text-gray-600">
                        Proceed with only the valid contacts. Invalid entries
                        will be automatically removed.
                      </p>
                      <button
                        onClick={proceedWithCleanedFile}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Use Cleaned Data
                      </button>
                    </div>

                    {/* Proceed with Original File Option */}
                    {/* <div
                      className={`p-4 rounded-lg border transition-colors ${
                        (validationResults?.failedRows?.filter(
                          (row) => row.hasSpaces && row.cleanPhone
                        ).length || 0) > 0
                          ? "border-gray-200 bg-gray-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <div className="flex gap-3 items-center mb-3">
                        <div
                          className={`flex justify-center items-center w-8 h-8 rounded-lg ${
                            (validationResults?.failedRows?.filter(
                              (row) => row.hasSpaces && row.cleanPhone
                            ).length || 0) > 0
                              ? "bg-gray-100"
                              : "bg-orange-100"
                          }`}
                        >
                          <FileText
                            className={`w-4 h-4 ${
                              (validationResults?.failedRows?.filter(
                                (row) => row.hasSpaces && row.cleanPhone
                              ).length || 0) > 0
                                ? "text-gray-400"
                                : "text-orange-600"
                            }`}
                          />
                        </div>
                        <h4
                          className={`font-medium ${
                            (validationResults?.failedRows?.filter(
                              (row) => row.hasSpaces && row.cleanPhone
                            ).length || 0) > 0
                              ? "text-gray-400"
                              : "text-gray-900"
                          }`}
                        >
                          Use Original File
                        </h4>
                      </div>
                      <p
                        className={`mb-4 text-sm ${
                          (validationResults?.failedRows?.filter(
                            (row) => row.hasSpaces && row.cleanPhone
                          ).length || 0) > 0
                            ? "text-gray-400"
                            : "text-gray-600"
                        }`}
                      >
                        {(validationResults?.failedRows?.filter(
                          (row) => row.hasSpaces && row.cleanPhone
                        ).length || 0) > 0
                          ? "Cannot use original file when phone numbers contain spaces"
                          : "Proceed with the original file as-is. Backend will handle validation and processing."}
                      </p>
                      <button
                        onClick={proceedWithOriginalFile}
                        disabled={
                          (validationResults?.failedRows?.filter(
                            (row) => row.hasSpaces && row.cleanPhone
                          ).length || 0) > 0
                        }
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors duration-200 font-medium text-sm ${
                          (validationResults?.failedRows?.filter(
                            (row) => row.hasSpaces && row.cleanPhone
                          ).length || 0) > 0
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-orange-600 hover:bg-orange-700 text-white"
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        Use Original File
                      </button>
                    </div> */}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <button
                  onClick={() => navigate("/contacts")}
                  className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium text-sm sm:text-base"
                  disabled={uploading || isProcessing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg sm:rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  disabled={
                    uploading ||
                    isProcessing ||
                    !formData.selectedFile ||
                    !formData.groupName.trim() ||
                    (showCleaningOptions && !userChoice)||
                       (validationResults && validationResults.successCount === 0)
                  }
                >
                  {uploading ? (
                    "Uploading..."
                  ) : (
                    <>
                      <span className="hidden sm:inline">
                        {showCleaningOptions && !userChoice
                          ? "Please Choose Option Above"
                          : "Upload File"}
                      </span>
                      <span className="sm:hidden">
                        {showCleaningOptions && !userChoice
                          ? "Choose Option"
                          : "Upload"}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Template Download */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl sm:rounded-2xl sm:p-6">
                <div className="flex gap-3 items-center mb-3 sm:mb-4">
                  <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-blue-600 rounded-lg sm:w-10 sm:h-10">
                    <Download className="w-4 h-4 text-white sm:w-5 sm:h-5" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                    Need a Template?
                  </h3>
                </div>
                <p className="mb-3 text-xs text-gray-600 sm:text-sm sm:mb-4">
                  Download our Excel template to ensure your contacts are
                  formatted correctly.
                  <strong>
                    Only Excel format (.xlsx/.xls) is supported
                  </strong>{" "}
                  for reliable processing.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Download Template</span>
                  <span className="sm:hidden">Template</span>
                </button>
              </div>

              {/* Quick Actions */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl sm:rounded-2xl sm:p-6">
                <div className="flex gap-3 items-center mb-3 sm:mb-4">
                  <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-purple-600 rounded-lg sm:w-10 sm:h-10">
                    <MessageSquare className="w-4 h-4 text-white sm:w-5 sm:h-5" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                    Ready to Send?
                  </h3>
                </div>
                <p className="mb-3 text-xs text-gray-600 sm:text-sm">
                  Once you've uploaded your contacts, you can start sending bulk
                  messages:
                </p>
                <button
                  onClick={() => navigate("/bulk-sms")}
                  className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
                >
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Go to Bulk SMS</span>
                  <span className="sm:hidden">Bulk SMS</span>
                </button>
              </div>

              {/* Excel Support Guide */}
              <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl sm:rounded-2xl sm:p-6">
                <div className="flex gap-3 items-center mb-3 sm:mb-4">
                  <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-red-600 rounded-lg sm:w-10 sm:h-10">
                    <CheckCircle2 className="w-4 h-4 text-white sm:w-5 sm:h-5" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                    Excel Files Supported!
                  </h3>
                </div>
                <p className="mb-3 text-xs text-gray-600 sm:text-sm">
                  You can now upload Excel files directly:
                </p>
                <div className="space-y-2 text-xs text-gray-700 sm:text-sm">
                  <div className="flex gap-2 items-start">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span>Upload .xlsx and .xls files directly</span>
                  </div>
                  <div className="flex gap-2 items-start">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span>
                      Phone numbers must be 12 digits starting with 233 (spaces
                      auto-removed)
                    </span>
                  </div>
                  <div className="flex gap-2 items-start">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span>No need to convert to CSV anymore</span>
                  </div>
                  <div className="flex gap-2 items-start">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span>Handles millions of records efficiently</span>
                  </div>
                </div>
              </div>

              {/* File Stats */}
              {formData.selectedFile && (
                <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
                  <div className="flex gap-3 items-center mb-3 sm:mb-4">
                    <div
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        fileStats.isLargeFile
                          ? "bg-yellow-100"
                          : "bg-purple-100"
                      }`}
                    >
                      <Users
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          fileStats.isLargeFile
                            ? "text-yellow-600"
                            : "text-purple-600"
                        }`}
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                      File Information
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 sm:text-sm">
                        File Size
                      </span>
                      <span className="text-xs font-medium text-gray-900 sm:text-sm">
                        {formatFileSize(formData.selectedFile.size)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 sm:text-sm">
                        Estimated Contacts
                      </span>
                      <span className="text-xs font-medium text-gray-900 sm:text-sm">
                        {formatNumber(fileStats.estimatedContacts)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 sm:text-sm">
                        Processing Method
                      </span>
                      <span
                        className={`text-xs sm:text-sm font-medium ${
                          fileStats.isLargeFile
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {fileStats.isLargeFile ? "Background" : "Instant"}
                      </span>
                    </div>
                    {showResults && fileStats.processingTime > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600 sm:text-sm">
                          Processing Time
                        </span>
                        <span className="text-xs font-medium text-gray-900 sm:text-sm">
                          {(fileStats.processingTime / 1000).toFixed(1)}s
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ContactUpload;
